<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import { useTranslation } from 'i18next-vue'
import error401Illustration from '@/assets/images/illustrations/errors/error-401.svg'
import error403Illustration from '@/assets/images/illustrations/errors/error-403.svg'
import error404Illustration from '@/assets/images/illustrations/errors/error-404.svg'
import error500Illustration from '@/assets/images/illustrations/errors/error-500.svg'

export type ErrorStatus = 401 | 403 | 404 | 500

const props = withDefaults(
    defineProps<{ status?: ErrorStatus; showBackButton?: boolean; fullscreen?: boolean }>(),
    {
        status: 404,
        fullscreen: false,
        // WHY: Explicit undefined disables Vue's boolean casting so the status-based default below applies.
        showBackButton: undefined,
    }
)

// WHY: Going back does not help when the CMS itself is failing.
const isBackButtonVisible = computed(() => props.showBackButton ?? props.status !== 500)

const router = useRouter()
const { t } = useTranslation()

const ILLUSTRATIONS: Record<ErrorStatus, string> = {
    401: error401Illustration,
    403: error403Illustration,
    404: error404Illustration,
    500: error500Illustration,
}

const illustration = computed(() => ILLUSTRATIONS[props.status])
const translationKey = computed(() => `frontend.errorPages.${props.status}`)

// WHY: `history.state.back` is only set by vue-router for in-app entries, so we never leave the domain.
const hasPreviousRoute = Boolean(router.options.history.state.back)
</script>

<template>
    <main
        :class="[
            'flex items-center justify-center bg-primary-50 px-4 py-16',
            fullscreen ? 'min-h-dvh' : 'min-h-[70vh]',
        ]"
        :data-testid="`error-view-${status}`"
    >
        <section class="w-full max-w-xl" aria-labelledby="error-view-title">
            <img :src="illustration" alt="" class="mx-auto mb-16 h-56 w-auto" />
            <h1 id="error-view-title" class="heading-3xl mb-6 text-surface-900">
                {{ status }} – {{ t(`${translationKey}.title`) }}
            </h1>
            <p class="paragraph mb-8 text-surface-900">
                {{ t(`${translationKey}.description`) }}
            </p>
            <template v-if="isBackButtonVisible">
                <Button
                    v-if="hasPreviousRoute"
                    :label="t('frontend.errorPages.actions.back')"
                    data-testid="error-view-back-button"
                    @click="router.back()"
                />
                <Button
                    v-else
                    as="router-link"
                    :to="{ name: 'home' }"
                    :label="t('frontend.errorPages.actions.back')"
                    data-testid="error-view-back-button"
                />
            </template>
        </section>
    </main>
</template>
